import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { 
  ArrowLeft, Newspaper, Calendar, Tag, Clock,
  ChevronDown, Search, Filter
} from 'lucide-react';
import Card from '../components/common/Card';
import Avatar from '../components/common/Avatar';
import { formatRelativeTime } from '../utils/formatters';

// Sample blog data
const blogPosts = [
  {
    id: '1',
    title: 'Expanding Our Impact: New Training Centers in Africa',
    excerpt: 'Announcing the launch of five new training centers across Africa, bringing tech education to underserved communities.',
    content: 'Full content here...',
    category: 'Announcements',
    tags: ['expansion', 'africa', 'education'],
    author: {
      name: 'Sarah Mitchell',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
      role: 'CEO'
    },
    date: new Date('2024-03-15'),
    readTime: 5,
    featured: true,
    image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1200'
  },
  {
    id: '2',
    title: 'Q1 2024 Impact Report: Transforming Lives Through Technology',
    excerpt: 'A detailed look at our achievements and impact in the first quarter of 2024.',
    content: 'Full content here...',
    category: 'Reports',
    tags: ['impact', 'quarterly-report', 'metrics'],
    author: {
      name: 'David Kim',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150',
      role: 'Impact Director'
    },
    date: new Date('2024-03-10'),
    readTime: 8,
    featured: true,
    image: 'https://images.unsplash.com/photo-1553484771-047a44eee27b?w=1200'
  },
  {
    id: '3',
    title: 'Partnership Announcement: Major Tech Companies Join Our Mission',
    excerpt: 'Leading technology companies commit to supporting our workforce development initiatives.',
    content: 'Full content here...',
    category: 'Partnerships',
    tags: ['partnership', 'industry', 'collaboration'],
    author: {
      name: 'Emma Wilson',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150',
      role: 'Partnership Director'
    },
    date: new Date('2024-03-05'),
    readTime: 4,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200'
  }
];

const categories = [
  'All',
  'Announcements',
  'Reports',
  'Partnerships',
  'Events',
  'Success Stories'
];

export default function BlogPage() {
  const { theme } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = filteredPosts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Return to About Link */}
        <Link
          to="/about"
          className={`
            inline-flex items-center space-x-2 mb-8 group
            ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}
          `}
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          <span>Return to About Us</span>
        </Link>

        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-4">
            <Newspaper className="h-12 w-12 text-blue-500" />
          </div>
          <h1 className={`text-4xl font-bold mb-4 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Latest Updates
          </h1>
          <p className={`text-xl max-w-3xl mx-auto ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Stay informed about our latest initiatives, impact reports, and success stories
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-8">
          <div className="relative flex-1 w-full">
            <Search className={`absolute left-3 top-3 h-5 w-5 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`
                w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                }
              `}
            />
          </div>
          
          <div className="relative w-full sm:w-auto">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`
                w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none appearance-none
                ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                }
              `}
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <Filter className={`absolute right-3 top-3 h-5 w-5 pointer-events-none ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`} />
          </div>
        </div>

        {/* Featured Posts */}
        {featuredPosts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {featuredPosts.map(post => (
              <Card
                key={post.id}
                className="overflow-hidden group cursor-pointer"
                onClick={() => {/* Handle navigation */}}
              >
                <div className="relative aspect-[16/9]">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className={`
                    absolute top-4 left-4 px-3 py-1 rounded-full text-sm
                    ${theme === 'dark'
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-blue-50 text-blue-600'
                    }
                  `}>
                    Featured
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Avatar
                        src={post.author.avatar}
                        alt={post.author.name}
                        size="sm"
                      />
                      <div>
                        <p className={`text-sm font-medium ${
                          theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                        }`}>
                          {post.author.name}
                        </p>
                        <p className={`text-xs ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {post.author.role}
                        </p>
                      </div>
                    </div>
                    <div className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {formatRelativeTime(post.date)}
                    </div>
                  </div>

                  <h2 className={`text-xl font-bold mb-2 group-hover:text-blue-500 transition-colors ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    {post.title}
                  </h2>
                  
                  <p className={`mb-4 line-clamp-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm">
                      <div className={`flex items-center space-x-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <Tag className="h-4 w-4" />
                        <span>{post.category}</span>
                      </div>
                      <div className={`flex items-center space-x-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <Clock className="h-4 w-4" />
                        <span>{post.readTime} min read</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Regular Posts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {regularPosts.map(post => (
            <Card
              key={post.id}
              className="overflow-hidden group cursor-pointer"
              onClick={() => {/* Handle navigation */}}
            >
              <div className="relative aspect-[16/9]">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Avatar
                    src={post.author.avatar}
                    alt={post.author.name}
                    size="sm"
                  />
                  <div>
                    <p className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {post.author.name}
                    </p>
                    <p className={`text-xs ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {formatRelativeTime(post.date)}
                    </p>
                  </div>
                </div>

                <h3 className={`text-lg font-bold mb-2 line-clamp-2 group-hover:text-blue-500 transition-colors ${
                  theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  {post.title}
                </h3>

                <p className={`text-sm mb-4 line-clamp-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {post.excerpt}
                </p>

                <div className="flex items-center justify-between text-sm">
                  <span className={`px-2 py-1 rounded-full ${
                    theme === 'dark'
                      ? 'bg-gray-700 text-gray-300'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {post.category}
                  </span>
                  <div className={`flex items-center space-x-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <Clock className="h-4 w-4" />
                    <span>{post.readTime} min read</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}